import { useCallback, useEffect, useState } from 'react';
import {
  HardDrive,
  BarChart3,
  AlertTriangle,
  Loader2,
  RefreshCw,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from 'recharts';
import api from '@/services/api';
import { useCurrency } from '@/hooks/useCurrency';
import type { Receipt } from '@/lib/types';

interface StorageData {
  totalReceipts: number;
  totalOriginalSize: number;
  totalCompressedSize: number;
  compressionRatio: number;
  byCategory: { category: string; count: number; size: number }[];
  byMonth: { month: string; count: number; size: number }[];
}

interface CategoryEntry {
  category: string;
  count: number;
  totalAmount: number;
}

interface DuplicateGroup {
  group: string;
  receipts: Receipt[];
  reason: string;
}

const CHART_COLORS = [
  '#10b981',
  '#3b82f6',
  '#f59e0b',
  '#ef4444',
  '#8b5cf6',
  '#06b6d4',
  '#f97316',
  '#ec4899',
];

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function ReceiptInsights() {
  const { fmt } = useCurrency();
  const [tab, setTab] = useState('storage');

  const [storage, setStorage] = useState<StorageData | null>(null);
  const [storageLoading, setStorageLoading] = useState(false);

  const [categoryEntries, setCategoryEntries] = useState<CategoryEntry[]>([]);
  const [categoryLoading, setCategoryLoading] = useState(false);

  const [duplicates, setDuplicates] = useState<DuplicateGroup[]>([]);
  const [duplicateLoading, setDuplicateLoading] = useState(false);

  const fetchStorage = useCallback(async () => {
    setStorageLoading(true);
    try {
      const res = await api.get('/receipts/insights/storage');
      setStorage(res.data.data || res.data);
    } catch {
      /* ignore */
    }
    setStorageLoading(false);
  }, []);

  const fetchCategories = useCallback(async () => {
    setCategoryLoading(true);
    try {
      const res = await api.get('/receipts/insights/categories');
      const data = res.data.data || res.data;
      setCategoryEntries(data.categories || data || []);
    } catch {
      /* ignore */
    }
    setCategoryLoading(false);
  }, []);

  const fetchDuplicates = useCallback(async () => {
    setDuplicateLoading(true);
    try {
      const res = await api.get('/receipts/insights/duplicates');
      const data = res.data.data || res.data;
      setDuplicates(data.duplicates || data || []);
    } catch {
      /* ignore */
    }
    setDuplicateLoading(false);
  }, []);

  const storageFetched = storage !== null;
  const categoriesFetched = categoryEntries.length > 0;
  const duplicatesFetched = duplicates.length > 0;

  useEffect(() => {
    if (tab === 'storage' && !storageFetched) fetchStorage();
    if (tab === 'categories' && !categoriesFetched) fetchCategories();
    if (tab === 'duplicates' && !duplicatesFetched) fetchDuplicates();
  }, [
    tab,
    storageFetched,
    categoriesFetched,
    duplicatesFetched,
    fetchStorage,
    fetchCategories,
    fetchDuplicates,
  ]);

  return (
    <div className="space-y-4" data-testid="receipt-insights">
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="storage" className="min-h-[44px]">
            <HardDrive className="mr-2 h-4 w-4 hidden sm:inline-block" /> Storage
          </TabsTrigger>
          <TabsTrigger value="categories" className="min-h-[44px]">
            <BarChart3 className="mr-2 h-4 w-4 hidden sm:inline-block" /> Categories
          </TabsTrigger>
          <TabsTrigger value="duplicates" className="min-h-[44px]">
            <AlertTriangle className="mr-2 h-4 w-4 hidden sm:inline-block" /> Duplicates
          </TabsTrigger>
        </TabsList>

        {/* Storage Overview */}
        <TabsContent value="storage">
          {storageLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : storage ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                {[
                  { label: 'Total Receipts', value: storage.totalReceipts.toString() },
                  { label: 'Original Size', value: formatBytes(storage.totalOriginalSize) },
                  { label: 'Compressed', value: formatBytes(storage.totalCompressedSize) },
                  {
                    label: 'Space Saved',
                    value: `${Math.round(storage.compressionRatio * 100)}%`,
                  },
                ].map((kpi) => (
                  <div key={kpi.label} className="rounded-lg border bg-card p-3 text-center">
                    <p className="text-xs text-muted-foreground">{kpi.label}</p>
                    <p className="text-lg font-bold">{kpi.value}</p>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                {storage.byCategory.length > 0 && (
                  <div className="rounded-lg border bg-card p-4">
                    <p className="mb-2 text-sm font-medium">By Category</p>
                    <div className="min-w-0 h-48">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={storage.byCategory}
                            dataKey="count"
                            nameKey="category"
                            cx="50%"
                            cy="50%"
                            innerRadius={40}
                            outerRadius={70}
                            paddingAngle={2}
                          >
                            {storage.byCategory.map((_, i) => (
                              <Cell
                                key={`cell-${i}`}
                                fill={CHART_COLORS[i % CHART_COLORS.length]}
                              />
                            ))}
                          </Pie>
                          <Tooltip />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {storage.byCategory.map((cat, i) => (
                        <Badge key={cat.category} variant="outline" className="text-xs">
                          <span
                            className="mr-1 inline-block h-2 w-2 rounded-full"
                            style={{
                              backgroundColor: CHART_COLORS[i % CHART_COLORS.length],
                            }}
                          />
                          {cat.category} ({cat.count})
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {storage.byMonth.length > 0 && (
                  <div className="rounded-lg border bg-card p-4">
                    <p className="mb-2 text-sm font-medium">By Month</p>
                    <div className="min-w-0 h-48">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={storage.byMonth}>
                          <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                          <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                          <YAxis tick={{ fontSize: 11 }} />
                          <Tooltip />
                          <Line
                            type="monotone"
                            dataKey="count"
                            stroke="#3b82f6"
                            strokeWidth={2}
                            dot={{ r: 3 }}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                )}
              </div>

              <Button
                variant="outline"
                size="sm"
                className="min-h-[44px]"
                onClick={() => {
                  setStorage(null);
                  fetchStorage();
                }}
              >
                <RefreshCw className="mr-2 h-4 w-4" /> Refresh
              </Button>
            </div>
          ) : (
            <p className="py-8 text-center text-sm text-muted-foreground">
              No storage data available
            </p>
          )}
        </TabsContent>

        {/* Category Distribution */}
        <TabsContent value="categories">
          {categoryLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : categoryEntries.length > 0 ? (
            <div className="space-y-3">
              {/* Desktop table */}
              <div className="hidden md:block overflow-hidden rounded-lg border">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="px-4 py-2 text-left font-medium">Category</th>
                      <th className="px-4 py-2 text-right font-medium">Count</th>
                      <th className="px-4 py-2 text-right font-medium">Total Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {categoryEntries.map((cat) => (
                      <tr key={cat.category} className="border-b last:border-0">
                        <td className="px-4 py-2">{cat.category}</td>
                        <td className="px-4 py-2 text-right">{cat.count}</td>
                        <td className="px-4 py-2 text-right font-medium">
                          {fmt(cat.totalAmount)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile cards */}
              <div className="space-y-2 md:hidden">
                {categoryEntries.map((cat) => (
                  <div
                    key={cat.category}
                    className="flex items-center justify-between rounded-lg border p-3"
                  >
                    <div>
                      <p className="text-sm font-medium break-words">{cat.category}</p>
                      <p className="text-xs text-muted-foreground">
                        {cat.count} receipt{cat.count !== 1 ? 's' : ''}
                      </p>
                    </div>
                    <p className="text-sm font-bold">{fmt(cat.totalAmount)}</p>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <p className="py-8 text-center text-sm text-muted-foreground">
              No category data available
            </p>
          )}
        </TabsContent>

        {/* Duplicate Detection */}
        <TabsContent value="duplicates">
          {duplicateLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : duplicates.length > 0 ? (
            <div className="space-y-3">
              {duplicates.map((group) => (
                <div key={group.group} className="space-y-2 rounded-lg border p-3">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4 text-amber-500" />
                    <p className="text-sm font-medium">Potential Duplicate</p>
                  </div>
                  <p className="text-xs text-muted-foreground">{group.reason}</p>
                  <div className="flex flex-wrap gap-2">
                    {group.receipts.map((r) => (
                      <Badge key={r._id} variant="outline" className="text-xs">
                        {r.metadata.merchant || r.originalFilename}
                        {r.metadata.total != null && ` — ${fmt(r.metadata.total)}`}
                      </Badge>
                    ))}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="min-h-[44px]"
                    data-testid={`review-duplicate-${group.group}`}
                  >
                    Review
                  </Button>
                </div>
              ))}
            </div>
          ) : (
            <p className="py-8 text-center text-sm text-muted-foreground">
              No potential duplicates found
            </p>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
